<template>
  <div class="vivo">
    <div v-if="loading" class="state">{{ $t("policy-contract.loading") }}</div>
    <div v-else-if="error" class="state">{{ $t("policy-contract.error") }}</div>
    <template v-else>
      <!-- top bar -->
      <div class="row between" style="margin-bottom: 14px">
        <div>
          <div class="pagetitle">{{ $t("policy-contract.title") }}</div>
          <div class="crumb">{{ $t("common.company") }} / {{ $t("policy-contract.title") }}</div>
        </div>
        <span v-if="selectedPolicy" class="tag" :class="statusTag(selectedPolicy.status)">
          <span class="dotc"></span> {{ statusText(selectedPolicy.status) }}
        </span>
      </div>

      <!-- company policies (chip bar) -->
      <div class="panel" style="margin-bottom: 14px">
        <div class="panel__head">
          <h4>📑 {{ $t("policy-contract.policiesPanel") }}</h4>
          <span class="tag muted">{{ $t("policy-contract.policiesCount", { n: policies.length }) }}</span>
        </div>
        <div class="panel__body">
          <div v-if="policies.length" class="row" style="gap: 8px; flex-wrap: wrap">
            <button
              v-for="p in policies"
              :key="p.id"
              type="button"
              class="chip"
              :class="{ on: p.id === selectedId }"
              @click="selectedId = p.id"
            >
              🩺 {{ p.number }}
              <span class="tag" :class="statusTag(p.status)">{{ statusText(p.status) }}</span>
            </button>
          </div>
          <p v-else class="xs muted">{{ $t("policy-contract.noPolicies") }}</p>
          <p class="xs muted mt">{{ $t("policy-contract.pickHint") }}</p>
        </div>
      </div>

      <template v-if="selectedPolicy">
        <!-- policy terms + signed contract -->
        <div class="grid2">
          <div class="panel">
            <div class="panel__head">
              <h4>
                🛡️ {{ $t("policy-contract.conditions") }}
                <span v-if="selectedPolicy.classLabel" class="tag purple">{{ selectedPolicy.classLabel }}</span>
              </h4>
              <span class="tag paid">{{ $t("policy-contract.readOnly") }}</span>
            </div>
            <div class="panel__body">
              <div class="row between">
                <span class="muted sm">{{ $t("policy-contract.number") }}</span>
                <b class="mono">{{ selectedPolicy.number }}</b>
              </div>
              <div class="divider"></div>
              <div class="row between">
                <span class="muted sm">{{ $t("policy-contract.klass") }}</span>
                <b>{{ selectedPolicy.classLabel || "—" }}</b>
              </div>
              <div class="divider"></div>
              <div class="row between">
                <span class="muted sm">{{ $t("policy-contract.validity") }}</span>
                <b>{{ fDate(selectedPolicy.validFrom) }} — {{ fDate(selectedPolicy.validTo) }}</b>
              </div>
              <div class="divider"></div>
              <div class="row between">
                <span class="muted sm">{{ $t("policy-contract.annualCap") }}</span>
                <b class="mono">{{ fMoney(selectedPolicy.annualCap) }}</b>
              </div>
              <div class="divider"></div>
              <div class="row between">
                <span class="muted sm">{{ $t("policy-contract.deductible") }}</span>
                <b class="mono">{{ fPct(selectedPolicy.deductiblePct) }}</b>
              </div>
              <div class="divider"></div>
              <div class="row between">
                <span class="muted sm">{{ $t("policy-contract.statusLabel") }}</span>
                <b><span class="tag" :class="statusTag(selectedPolicy.status)">{{ statusText(selectedPolicy.status) }}</span></b>
              </div>
            </div>
          </div>

          <div class="panel">
            <div class="panel__head"><h4>📑 {{ $t("policy-contract.contract") }}</h4></div>
            <div class="panel__body">
              <template v-if="contract">
                <div class="upload" style="border-style: solid">
                  <div class="ic">📄</div>
                  <b>{{ contract.fileRef }}</b>
                  <div class="xs muted mt">{{ $t("policy-contract.signedShort") }} {{ fDate(contract.signedAt) }}</div>
                </div>
                <div class="row mt" style="gap: 10px">
                  <button
                    class="btn primary sm"
                    type="button"
                    disabled
                    :title="$t('policy-contract.fileUnavailable')"
                  >
                    👁️ {{ $t("policy-contract.preview") }}
                  </button>
                  <button
                    class="btn soft sm"
                    type="button"
                    disabled
                    :title="$t('policy-contract.fileUnavailable')"
                  >
                    ⬇️ {{ $t("policy-contract.download") }}
                  </button>
                </div>
                <p class="xs muted mt">{{ $t("policy-contract.fileUnavailable") }}</p>
                <div class="divider"></div>
                <div class="row between">
                  <span class="muted sm">{{ $t("policy-contract.signedAt") }}</span>
                  <b>{{ fDate(contract.signedAt) }}</b>
                </div>
                <div class="divider"></div>
                <div class="row between">
                  <span class="muted sm">{{ $t("policy-contract.broker") }}</span>
                  <b>{{ contract.broker || "—" }}</b>
                </div>
              </template>
              <p v-else class="xs muted">{{ $t("policy-contract.noContract") }}</p>
            </div>
          </div>
        </div>

        <!-- coverages + exclusions -->
        <div class="grid2 mt">
          <div class="panel">
            <div class="panel__head"><h4>✅ {{ $t("policy-contract.coverages") }}</h4></div>
            <div class="panel__body">
              <div v-if="resolvedCoverages.length && coveragesLoaded" class="row" style="gap: 8px; flex-wrap: wrap">
                <span v-for="c in resolvedCoverages" :key="c.id" class="tag ok">{{ c.name || "#" + c.id }}</span>
              </div>
              <p v-else-if="resolvedCoverages.length && !coveragesLoaded" class="xs muted">
                {{ $t("policy-contract.coveragesUnavailable") }}
              </p>
              <p v-else class="xs muted">{{ $t("policy-contract.noCoverages") }}</p>
            </div>
          </div>

          <div class="panel">
            <div class="panel__head"><h4>🚫 {{ $t("policy-contract.exclusions") }}</h4></div>
            <div class="panel__body">
              <div v-if="selectedPolicy.exclusions" class="row" style="gap: 8px; flex-wrap: wrap">
                <span class="tag danger">{{ selectedPolicy.exclusions }}</span>
              </div>
              <p v-else class="xs muted">{{ $t("policy-contract.noExclusions") }}</p>
              <div class="note info mt">
                <b>{{ $t("policy-contract.noteTitle") }}</b>{{ $t("policy-contract.exclusionsNote") }}
              </div>
            </div>
          </div>
        </div>

      </template>
    </template>
  </div>
</template>

<script>
import { apiGet } from "../../api/vivo/http";
import { companyPath } from "../../api/vivo/session";
import { resolveCoverages, selectPolicy, statusMeta } from "./policy-contract-vm";
// F1 (HR issues 2026-10-16) «البوليصة والعقد شيل شجرة العقد»: the read-only
// contract tree is gone from this screen. The tree still exists where it is
// actually used — the admin portal edits it, and employee-add.vue reads it for
// the employee tier dropdown — so the backend endpoint is untouched.

export default {
  name: "VivoPolicyContract",
  data() {
    return {
      policies: [],
      contract: null,
      coverages: [],
      selectedId: null,
      loading: true,
      error: false,
    };
  },
  async mounted() {
    try {
      const [policiesRes, contract] = await Promise.all([apiGet(companyPath("policies")), apiGet(companyPath("contract"))]);
      this.policies = (policiesRes && policiesRes.data) || [];
      this.contract = contract || null;
      const routeId = this.$route && this.$route.params ? this.$route.params.id : null;
      const initial = selectPolicy(this.policies, routeId);
      this.selectedId = initial ? initial.id : null;
      // Coverage names are resolved from GET /coverages; tolerate a gap in that
      // endpoint so the policy + contract still render if it is unavailable.
      try {
        const covRes = await apiGet("/coverages");
        this.coverages = (covRes && covRes.data) || [];
      } catch (e) {
        this.coverages = [];
      }
    } catch (e) {
      this.error = true;
    } finally {
      this.loading = false;
    }
  },
  computed: {
    selectedPolicy() {
      return selectPolicy(this.policies, this.selectedId);
    },
    coveragesLoaded() {
      return this.coverages.length > 0;
    },
    resolvedCoverages() {
      const p = this.selectedPolicy;
      return p ? resolveCoverages(p.coverageIds, this.coverages, this.$i18n.locale) : [];
    },
  },
  methods: {
    statusTag(status) {
      return statusMeta(status).tag;
    },
    statusText(status) {
      return this.$t("policy-contract." + statusMeta(status).key);
    },
    fMoney(n) {
      return new Intl.NumberFormat(this.$i18n.locale, {
        style: "currency",
        currency: "EGP",
        maximumFractionDigits: 0,
        numberingSystem: "latn",
      }).format(n || 0);
    },
    fPct(n) {
      return new Intl.NumberFormat(this.$i18n.locale, { numberingSystem: "latn" }).format(n || 0) + "%";
    },
    fDate(iso) {
      if (!iso) return "—";
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
        numberingSystem: "latn",
      }).format(new Date(iso));
    },
  },
};
</script>
